(() => {
  'use strict';

  if (window.TSSOrderInformationInitialised) {
    return;
  }

  window.TSSOrderInformationInitialised = true;

  const storefrontFetch = window.fetch.bind(window);

  const shopRoot =
    window.Shopify &&
    window.Shopify.routes &&
    window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';

  let cartResetPromise = null;
  let lastCartResetTime = 0;

  async function updateCartAttributes(attributes) {
    const response = await storefrontFetch(
      `${shopRoot}cart/update.js`,
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ attributes })
      }
    );

    if (!response.ok) {
      throw new Error(
        `Unable to update cart attributes: ${response.status}`
      );
    }

    return response.json();
  }

  function setCompletionDatasets(
    deliveryValue,
    pickupValue
  ) {
    document
      .querySelectorAll(
        '[data-tss-delivery-information]'
      )
      .forEach((container) => {
        container.dataset.tssSavedDeliveryComplete =
          deliveryValue;

        container.dataset.tssSavedPickupComplete =
          pickupValue;
      });
  }

  function showCartChangedMessage() {
    document
      .querySelectorAll(
        '[data-tss-delivery-information]'
      )
      .forEach((container) => {
        const errorBox = container.querySelector(
          '[data-tss-delivery-error]'
        );

        const successBox = container.querySelector(
          '[data-tss-delivery-success]'
        );

        if (successBox) {
          successBox.hidden = true;
        }

        if (errorBox) {
          errorBox.textContent =
            'Your cart has changed. Please review and save the required order information again before checkout.';

          errorBox.hidden = false;
        }
      });
  }

  async function resetOrderReviewAfterCartChange() {
    const currentTime = Date.now();

    if (
      cartResetPromise ||
      currentTime - lastCartResetTime < 1500
    ) {
      return cartResetPromise;
    }

    lastCartResetTime = currentTime;

    cartResetPromise = updateCartAttributes({
      'Delivery information complete': '',
      'Pickup information complete': ''
    })
      .then(() => {
        setCompletionDatasets('', '');
        showCartChangedMessage();

        document.dispatchEvent(
          new CustomEvent(
            'tss:order-information-reset'
          )
        );
      })
      .catch((error) => {
        console.warn(
          'Order information review status could not be reset.',
          error
        );
      })
      .finally(() => {
        cartResetPromise = null;
      });

    return cartResetPromise;
  }

  function requestUrl(resource) {
    if (typeof resource === 'string') {
      return resource;
    }

    if (
      resource &&
      typeof resource.url === 'string'
    ) {
      return resource.url;
    }

    return '';
  }

  function requestMethod(resource, options) {
    if (
      options &&
      typeof options.method === 'string'
    ) {
      return options.method.toUpperCase();
    }

    if (
      resource &&
      typeof resource.method === 'string'
    ) {
      return resource.method.toUpperCase();
    }

    return 'GET';
  }

  function bodyContainsLineUpdates(body) {
    if (!body) {
      return true;
    }

    if (typeof body === 'string') {
      try {
        const parsedBody = JSON.parse(body);

        return (
          Object.prototype.hasOwnProperty.call(
            parsedBody,
            'updates'
          ) ||
          (
            Object.prototype.hasOwnProperty.call(
              parsedBody,
              'id'
            ) &&
            Object.prototype.hasOwnProperty.call(
              parsedBody,
              'quantity'
            )
          )
        );
      } catch (error) {
        return (
          body.includes('updates%5B') ||
          body.includes('updates[') ||
          body.includes('quantity=')
        );
      }
    }

    if (body instanceof FormData) {
      for (const key of body.keys()) {
        if (
          key === 'updates' ||
          key.startsWith('updates[') ||
          key === 'quantity'
        ) {
          return true;
        }
      }

      return false;
    }

    if (body instanceof URLSearchParams) {
      for (const key of body.keys()) {
        if (
          key === 'updates' ||
          key.startsWith('updates[') ||
          key === 'quantity'
        ) {
          return true;
        }
      }

      return false;
    }

    return true;
  }

  function requestChangesCartLines(
    resource,
    options = {}
  ) {
    const url = requestUrl(resource);
    const method = requestMethod(
      resource,
      options
    );

    if (method === 'GET') {
      return false;
    }

    if (
      /\/cart\/(?:add|change|clear)(?:\.js)?(?:[?#]|$)/.test(
        url
      )
    ) {
      return true;
    }

    if (
      /\/cart\/update(?:\.js)?(?:[?#]|$)/.test(
        url
      )
    ) {
      return bodyContainsLineUpdates(
        options.body
      );
    }

    return false;
  }

  window.fetch = async (...argumentsList) => {
    const resource = argumentsList[0];
    const options = argumentsList[1] || {};

    if (
      requestChangesCartLines(
        resource,
        options
      )
    ) {
      await resetOrderReviewAfterCartChange();
    }

    return storefrontFetch(...argumentsList);
  };

  const originalXhrOpen =
    window.XMLHttpRequest.prototype.open;

  const originalXhrSend =
    window.XMLHttpRequest.prototype.send;

  window.XMLHttpRequest.prototype.open =
    function (
      method,
      url,
      ...remainingArguments
    ) {
      this.tssRequestMethod =
        typeof method === 'string'
          ? method.toUpperCase()
          : 'GET';

      this.tssRequestUrl =
        typeof url === 'string'
          ? url
          : '';

      return originalXhrOpen.call(
        this,
        method,
        url,
        ...remainingArguments
      );
    };

  window.XMLHttpRequest.prototype.send =
    function (body) {
      const xhr = this;

      const changesCartLines =
        requestChangesCartLines(
          {
            url: xhr.tssRequestUrl,
            method: xhr.tssRequestMethod
          },
          { body }
        );

      if (changesCartLines) {
        resetOrderReviewAfterCartChange()
          .finally(() => {
            originalXhrSend.call(
              xhr,
              body
            );
          });

        return;
      }

      return originalXhrSend.call(
        xhr,
        body
      );
    };

  document.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      if (
        event.promise &&
        typeof event.promise.then === 'function'
      ) {
        event.promise
          .then(() => {
            resetOrderReviewAfterCartChange();
          })
          .catch(() => {
            // Do not reset when the cart change failed.
          });

        return;
      }

      resetOrderReviewAfterCartChange();
    }
  );

  document.addEventListener(
    'change',
    (event) => {
      const field = event.target;

      if (!(field instanceof Element)) {
        return;
      }

      if (
        field.matches(
          [
            'input[name^="updates"]',
            'select[name^="updates"]',
            '[data-quantity-input]',
            '[data-cart-quantity]'
          ].join(',')
        )
      ) {
        resetOrderReviewAfterCartChange();
      }
    },
    true
  );

  document.addEventListener(
    'click',
    (event) => {
      const target =
        event.target instanceof Element
          ? event.target.closest(
              [
                'a[href*="/cart/change"][href*="quantity=0"]',
                '[data-cart-remove]',
                '[data-remove-item]'
              ].join(',')
            )
          : null;

      if (target) {
        resetOrderReviewAfterCartChange();
      }
    },
    true
  );

  function initialiseForm(container) {
    if (
      container.dataset.tssOrderInitialised ===
      'true'
    ) {
      return;
    }

    container.dataset.tssOrderInitialised =
      'true';

    const orderMethodInputs =
      container.querySelectorAll(
        '[data-tss-order-method]'
      );

    const deliveryFields =
      container.querySelector(
        '[data-tss-delivery-fields]'
      );

    const pickupFields =
      container.querySelector(
        '[data-tss-pickup-fields]'
      );

    const pickupConfirmation =
      container.querySelector(
        '[data-tss-pickup-confirmation]'
      );

    const addressTypeInputs =
      container.querySelectorAll(
        '[data-tss-address-type]'
      );

    const authorityInputs =
      container.querySelectorAll(
        '[data-tss-atl]'
      );

    const atlLocationGroup =
      container.querySelector(
        '[data-tss-atl-location-group]'
      );

    const atlLocationInput =
      container.querySelector(
        '[data-tss-atl-location]'
      );

    const signageGroup =
      container.querySelector(
        '[data-tss-business-signage]'
      );

    const signageInput =
      container.querySelector(
        '[data-tss-building-signage]'
      );

    const signageNumber =
      container.querySelector(
        '[data-tss-signage-number]'
      );

    const saveButton =
      container.querySelector(
        '[data-tss-delivery-save]'
      );

    const errorBox =
      container.querySelector(
        '[data-tss-delivery-error]'
      );

    const successBox =
      container.querySelector(
        '[data-tss-delivery-success]'
      );

    if (
      !orderMethodInputs.length ||
      !deliveryFields ||
      !pickupFields ||
      !pickupConfirmation ||
      !addressTypeInputs.length ||
      !authorityInputs.length ||
      !atlLocationGroup ||
      !atlLocationInput ||
      !signageGroup ||
      !signageInput ||
      !saveButton
    ) {
      return;
    }

    let incompleteTimer = null;
    let resetGeneration = 0;

    function selectedValue(inputs) {
      const selected =
        Array.from(inputs).find(
          (input) => input.checked
        );

      return selected
        ? selected.value
        : '';
    }

    function hideMessages() {
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }

      if (successBox) {
        successBox.hidden = true;
      }
    }

    function showError(message, field) {
      if (errorBox) {
        errorBox.textContent = message;
        errorBox.hidden = false;
      }

      if (successBox) {
        successBox.hidden = true;
      }

      if (
        field &&
        typeof field.focus === 'function'
      ) {
        field.focus();
      }
    }

    function updateConditionalFields() {
      const orderMethod =
        selectedValue(orderMethodInputs);

      const addressType =
        selectedValue(addressTypeInputs);

      const authorityToLeave =
        selectedValue(authorityInputs);

      const isDelivery =
        orderMethod === 'Delivery';

      const isPickup =
        orderMethod === 'Pick up';

      const isBusiness =
        addressType === 'Business';

      const hasAuthorityToLeave =
        authorityToLeave === 'Yes';

      deliveryFields.hidden = !isDelivery;
      pickupFields.hidden = !isPickup;

      atlLocationGroup.hidden =
        !isDelivery ||
        !hasAuthorityToLeave;

      signageGroup.hidden =
        !isDelivery ||
        !isBusiness;

      if (
        isDelivery &&
        hasAuthorityToLeave
      ) {
        atlLocationInput.setAttribute(
          'required',
          'required'
        );
      } else {
        atlLocationInput.removeAttribute(
          'required'
        );
      }

      if (
        isDelivery &&
        isBusiness
      ) {
        signageInput.setAttribute(
          'required',
          'required'
        );
      } else {
        signageInput.removeAttribute(
          'required'
        );
      }

      if (isPickup) {
        pickupConfirmation.setAttribute(
          'required',
          'required'
        );
      } else {
        pickupConfirmation.removeAttribute(
          'required'
        );
      }

      if (signageNumber) {
        signageNumber.textContent =
          hasAuthorityToLeave
            ? '5'
            : '4';
      }
    }

    function markInformationIncomplete() {
      hideMessages();

      resetGeneration += 1;

      const thisResetGeneration =
        resetGeneration;

      window.clearTimeout(
        incompleteTimer
      );

      incompleteTimer =
        window.setTimeout(async () => {
          if (
            thisResetGeneration !==
            resetGeneration
          ) {
            return;
          }

          try {
            await updateCartAttributes({
              'Delivery information complete': '',
              'Pickup information complete': ''
            });

            container.dataset
              .tssSavedDeliveryComplete = '';

            container.dataset
              .tssSavedPickupComplete = '';
          } catch (error) {
            console.warn(
              'Order information completion status could not be reset.',
              error
            );
          }
        }, 250);
    }

    orderMethodInputs.forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          updateConditionalFields();
          markInformationIncomplete();
        }
      );
    });

    addressTypeInputs.forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          updateConditionalFields();
          markInformationIncomplete();
        }
      );
    });

    authorityInputs.forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          updateConditionalFields();
          markInformationIncomplete();
        }
      );
    });

    pickupConfirmation.addEventListener(
      'change',
      markInformationIncomplete
    );

    atlLocationInput.addEventListener(
      'input',
      markInformationIncomplete
    );

    signageInput.addEventListener(
      'input',
      markInformationIncomplete
    );

    saveButton.addEventListener(
      'click',
      async () => {
        resetGeneration += 1;

        window.clearTimeout(
          incompleteTimer
        );

        incompleteTimer = null;

        hideMessages();

        const orderMethod =
          selectedValue(
            orderMethodInputs
          );

        const addressType =
          selectedValue(
            addressTypeInputs
          );

        const authorityToLeave =
          selectedValue(
            authorityInputs
          );

        const atlLocation =
  atlLocationInput.value
    .replace(/\s+/g, ' ')
    .trim();

const buildingSignage =
  signageInput.value
    .replace(/\s+/g, ' ')
    .trim();

        if (
          orderMethod !== 'Delivery' &&
          orderMethod !== 'Pick up'
        ) {
          showError(
            'Please select Delivery or Pick up.',
            orderMethodInputs[0]
          );

          return;
        }

        let attributes;
        let successMessage;

        if (orderMethod === 'Delivery') {
          if (
            addressType !== 'Residential' &&
            addressType !== 'Business'
          ) {
            showError(
              'Please select whether the delivery address is residential or business.',
              addressTypeInputs[0]
            );

            return;
          }

          if (
            authorityToLeave !== 'Yes' &&
            authorityToLeave !== 'No'
          ) {
            showError(
              'Please select whether Authority to Leave is provided.',
              authorityInputs[0]
            );

            return;
          }

          if (
            authorityToLeave === 'Yes' &&
            atlLocation === ''
          ) {
            showError(
              'Please enter where the courier should leave the order.',
              atlLocationInput
            );

            return;
          }

          if (
            addressType === 'Business' &&
            buildingSignage === ''
          ) {
            showError(
              'Please enter the business name or building signage that the courier should look for.',
              signageInput
            );

            return;
          }

          const supplierDeliveryInstructions = [
            'Order fulfilment method: Delivery',
            `Delivery address type: ${addressType}`,
            `Authority to Leave: ${authorityToLeave}`,

            authorityToLeave === 'Yes'
              ? `Authority to Leave location: ${atlLocation}`
              : null,

            addressType === 'Business'
              ? `Business or building signage: ${buildingSignage}`
              : null
          ]
            .filter(Boolean)
            .join('\n');

          attributes = {
            'Order fulfilment method':
              'Delivery',

            'Delivery address type':
              addressType,

            'Authority to leave':
              authorityToLeave,

            'Authority to leave location':
              authorityToLeave === 'Yes'
                ? atlLocation
                : '',

            'Building signage':
              addressType === 'Business'
                ? buildingSignage
                : '',

            'Supplier delivery instructions':
              supplierDeliveryInstructions,

            'Pickup confirmation':
              '',

            'Pickup information complete':
              '',

            'Delivery information complete':
              'Yes'
          };

          successMessage =
            'Delivery information saved. Select Checkout to continue.';
        } else {
          if (!pickupConfirmation.checked) {
            showError(
              'Please confirm the pickup information before continuing.',
              pickupConfirmation
            );

            return;
          }

          attributes = {
            'Order fulfilment method':
              'Pick up',

            'Pickup confirmation':
              'Yes',

            'Pickup information complete':
              'Yes',

            'Delivery information complete':
              '',

            'Delivery address type':
              '',

            'Authority to leave':
              '',

            'Authority to leave location':
              '',

            'Building signage':
              '',

            'Supplier delivery instructions':
              'Order fulfilment method: Pick up'
          };

          successMessage =
            'Pickup information saved. Select Checkout to continue.';
        }

        const originalButtonText =
          saveButton.textContent;

        saveButton.disabled = true;
        saveButton.textContent =
          'Saving…';

        try {
          await updateCartAttributes(
            attributes
          );

          if (orderMethod === 'Delivery') {
            container.dataset
              .tssSavedDeliveryComplete =
              'Yes';

            container.dataset
              .tssSavedPickupComplete =
              '';
          } else {
            container.dataset
              .tssSavedDeliveryComplete =
              '';

            container.dataset
              .tssSavedPickupComplete =
              'Yes';
          }

          if (successBox) {
            successBox.textContent =
              successMessage;

            successBox.hidden = false;
          }

          document.dispatchEvent(
            new CustomEvent(
              'tss:order-information-saved',
              {
                detail: {
                  orderMethod
                }
              }
            )
          );
        } catch (error) {
          console.error(error);

          showError(
            'Your order information could not be saved. Please try again.'
          );
        } finally {
          saveButton.disabled = false;

          saveButton.textContent =
            originalButtonText;
        }
      }
    );

    updateConditionalFields();

    const savedOrderMethod =
      selectedValue(orderMethodInputs);

    const legacyAttributes = {};

    if (
      savedOrderMethod !== 'Delivery' &&
      container.dataset
        .tssSavedDeliveryComplete === 'Yes'
    ) {
      legacyAttributes[
        'Delivery information complete'
      ] = '';
    }

    if (
      savedOrderMethod !== 'Pick up' &&
      container.dataset
        .tssSavedPickupComplete === 'Yes'
    ) {
      legacyAttributes[
        'Pickup information complete'
      ] = '';
    }

    if (
      Object.keys(legacyAttributes).length
    ) {
      updateCartAttributes(
        legacyAttributes
      ).catch((error) => {
        console.warn(
          'Old completion status could not be cleared.',
          error
        );
      });
    }
  }

  function initialiseAllForms() {
    document
      .querySelectorAll(
        '[data-tss-delivery-information]'
      )
      .forEach(initialiseForm);
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseAllForms,
      { once: true }
    );
  } else {
    initialiseAllForms();
  }

  document.addEventListener(
    'shopify:section:load',
    initialiseAllForms
  );
})();